import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { Patient } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

export interface EmergencyContact {
  contactName: string | null;
  contactPhone: string | null;
  contactRelationshipRoleType:
    | keyof typeof Patient.RelationshipRoleTypeLabels
    | null;
}

export interface GetEmergencyContactResponse extends EmergencyContact {
  patientId: string;
}

export const useGetEmergencyContact = (patientId?: string) => {
  return useApi<GetEmergencyContactResponse, EmergencyContact>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey:
        QueryKeys.Patient.MedicalRecords.Profile.DEFAULT_EMERGENCY_CONTACT(
          patientId,
        ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          `/clinic/patients/patient/${patientId}/emergency-contact`,
        );
        return response.data;
      },
      select: (data) => {
        return {
          contactName: data.contactName,
          contactPhone: data.contactPhone,
          contactRelationshipRoleType: data.contactRelationshipRoleType,
        };
      },
    },
  });
};

export const useUpdateEmergencyContact = (
  patientId?: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  const updateEmergencyContact = async (data: EmergencyContact) => {
    await axiosInstance.patch(
      `/clinic/patients/patient/${patientId}/emergency-contact`,
      data,
    );
  };

  return useApiMutation<void, EmergencyContact>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Profile.UPDATE_EMERGENCY_CONTACT(
          patientId,
        ),
      mutationFn: updateEmergencyContact,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Profile.DEFAULT_EMERGENCY_CONTACT(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};
